import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Tooltip, Chip } from "@mui/material";
import { Users, Mail, AlertCircle, X, Plus, AlertTriangle, Link2 } from "lucide-react";

import { Card, CardContent, CardHeader } from "../../components/Card";
import { AssignEntityModal } from "../../components/AssignEntityModal";
import { Avatar } from "../../components/Avatar";
import { EmptyStateMessage } from "../../components/EmptyStateMessage";
import { useAuth } from "../../components/contexts/AuthContext";
import { useTheme } from "../../theme/useTheme";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import { InfoTooltip } from "../../components/InfoTooltip";

const buildClientList = (clientUser) => {
  if (!clientUser) return [];
  if (Array.isArray(clientUser.clients)) return clientUser.clients;
  if (Array.isArray(clientUser.client_companies)) return clientUser.client_companies;
  return [];
};

const getClientUserName = (clientUser) => {
  if (!clientUser) return "";
  if (clientUser.full_name) return clientUser.full_name;
  return [clientUser.first_name, clientUser.last_name].filter(Boolean).join(" ");
};

const AssignClientModal = ({
  open,
  onClose,
  clientUser,
  allClients,
  isClientsLoading,
  onSuccess,
  onError,
}) => {
  const { theme } = useTheme();
  const [selectedClientId, setSelectedClientId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [removingClientId, setRemovingClientId] = useState(null);
  const [error, setError] = useState(null);
  const [localClients, setLocalClients] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedClientId("");
      setRemovingClientId(null);
      setError(null);
      setLocalClients(buildClientList(clientUser));
    }
  }, [open, clientUser]);

  const currentClientIds = useMemo(
    () => localClients.map((client) => client.id),
    [localClients]
  );

  const availableClients = useMemo(() => {
    const safeClients = Array.isArray(allClients) ? allClients : [];
    return safeClients.filter((client) => !currentClientIds.includes(client.id));
  }, [allClients, currentClientIds]);

  const handleAssignClient = async () => {
    if (!selectedClientId || !clientUser?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      await apiClient.post(`clients/${selectedClientId}/add-client-user/`, {
        user_id: clientUser.id,
      });

      const assignedClient = availableClients.find(
        (client) => String(client.id) === String(selectedClientId)
      );
      if (assignedClient) {
        setLocalClients((prev) => [...prev, assignedClient]);
      }
      setSelectedClientId("");
      onSuccess?.("assign", assignedClient);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Error al asignar cliente";
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveClient = async (clientId) => {
    if (!clientId || !clientUser?.id) return;
    if (localClients.length <= 1) return;

    setRemovingClientId(clientId);
    setError(null);

    try {
      await apiClient.post(`clients/${clientId}/remove-client-user/`, {
        user_id: clientUser.id,
      });

      setLocalClients((prev) => prev.filter((client) => client.id !== clientId));
      onSuccess?.("remove", { id: clientId });
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Error al desvincular cliente";
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setRemovingClientId(null);
    }
  };

  if (!clientUser) return null;

  const clientUserName = getClientUserName(clientUser) || clientUser.email;

  const currentFooter =
    localClients.length === 1 ? (
      <Typography
        variant="caption"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 1,
          color: theme.palette.text.secondary,
        }}
      >
        <AlertTriangle size={12} />
        Debe mantener al menos un cliente asignado
      </Typography>
    ) : null;

  return (
    <AssignEntityModal
      open={open}
      onClose={onClose}
      title={`Clientes asociados al usuario ${clientUserName}`}
      currentTitle="Clientes actuales"
      currentItems={localClients}
      renderCurrentItem={(client) => (
        <Chip
          key={client.id}
          icon={<Users size={14} />}
          label={client.name}
          onDelete={
            localClients.length > 1 ? () => handleRemoveClient(client.id) : undefined
          }
          deleteIcon={
            removingClientId === client.id ? (
              <CircularProgress size={14} />
            ) : (
              <X size={14} />
            )
          }
          disabled={removingClientId === client.id || localClients.length <= 1}
          sx={{
            backgroundColor: `${theme.palette.primary.main}15`,
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}30`,
            "& .MuiChip-icon": {
              color: theme.palette.primary.main,
            },
            "& .MuiChip-deleteIcon": {
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.error.main,
              },
            },
          }}
        />
      )}
      emptyCurrentText="Sin clientes asignados"
      currentFooter={currentFooter}
      assignTitle="Asociar a nuevo cliente"
      availableOptions={availableClients.map((client) => ({
        value: client.id,
        label: client.name,
      }))}
      selectedValue={selectedClientId}
      onSelectValue={setSelectedClientId}
      assignPlaceholder="Buscar cliente..."
      onAssign={handleAssignClient}
      assignDisabled={!selectedClientId || isLoading}
      assignLoading={isLoading}
      assignButtonLabel="Asociar"
      emptyAvailableText="Este usuario ya está asignado a todos los clientes disponibles"
      isOptionsLoading={isClientsLoading}
      loadingText="Cargando clientes..."
      error={error}
      onClearError={() => setError(null)}
    />
  );
};

const ClientUserCard = ({ clientUser, onAssignClients, canAssign }) => {
  const { theme } = useTheme();
  const clientUserName = getClientUserName(clientUser) || clientUser.email;
  const clientList = buildClientList(clientUser);
  const isMultiClient = clientList.length > 1;

  return (
    <Card
      sx={{
        cursor: "default",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[2],
        },
        p: 0,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 0, "&:only-child": { p: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Avatar
            name={clientUserName}
            src={clientUser.profile_image || undefined}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {clientUserName}
            </Typography>

            {clientUser.email && (
              <Box
                component="a"
                href={`mailto:${clientUser.email}`}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: theme.palette.text.secondary,
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  mb: 1,
                  transition: "color 0.2s ease-in-out",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    textDecoration: "underline",
                  },
                  "& svg": {
                    width: 14,
                    height: 14,
                  },
                }}
              >
                <Mail />
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontSize: "inherit" }}
                >
                  {clientUser.email}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              <Users size={16} style={{ color: theme.palette.text.secondary }} />
              {clientList.length > 0 ? (
                clientList.map((client) => (
                  <Tooltip key={client.id} title={client.name} arrow>
                    <Chip
                      label={client.name}
                      size="small"
                      sx={{
                        height: "20px",
                        fontSize: "0.7rem",
                        backgroundColor: isMultiClient
                          ? `${theme.palette.primary.main}15`
                          : theme.palette.action.hover,
                        color: isMultiClient
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        border: isMultiClient
                          ? `1px solid ${theme.palette.primary.main}30`
                          : "none",
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Sin clientes asignados
                </Typography>
              )}

              {canAssign && onAssignClients && (
                <Tooltip title="Click para vincular este usuario a otro cliente, de tal forma que el usuario pueda pertenecer a más de un cliente" arrow>
                  <Chip
                    icon={<Plus size={12} />}
                    label=""
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onAssignClients(clientUser);
                    }}
                    sx={{
                      height: "20px",
                      width: "28px",
                      minWidth: "28px",
                      cursor: "pointer",
                      backgroundColor: theme.palette.action.hover,
                      "&:hover": {
                        backgroundColor: theme.palette.action.selected,
                      },
                      "& .MuiChip-icon": {
                        margin: 0,
                      },
                      "& .MuiChip-label": {
                        display: "none",
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export function ClientUsersSection({
  clientId,
  refreshKey,
  onSuccess,
  onError,
  clientDetailData,
  clientDetailLoading,
  clientDetailError,
}) {
  const { user: currentUser, interceptorReady } = useAuth();
  const { theme, colors } = useTheme();
  const [refreshClientDetail, setRefreshClientDetail] = useState(0);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedClientUser, setSelectedClientUser] = useState(null);
  const [clientUsersLocal, setClientUsersLocal] = useState([]);
  const [clientNameLocal, setClientNameLocal] = useState("");
  const [hasLoadedClient, setHasLoadedClient] = useState(false);

  const canAssignClientUsers =
    currentUser?.role === "superadmin" || currentUser?.role === "agency_admin";

  const hasParentClientDetail =
    clientDetailData !== undefined ||
    clientDetailLoading !== undefined ||
    clientDetailError !== undefined;

  const shouldFetchClientDetail =
    !hasParentClientDetail && !!clientId && interceptorReady;

  const {
    data: fetchedClientDetailData,
    error: fetchedClientDetailError,
    isLoading: fetchedClientDetailLoading,
  } = getData(
    shouldFetchClientDetail ? `clients/${clientId}/` : null,
    shouldFetchClientDetail,
    [clientId, refreshKey, refreshClientDetail]
  );

  const resolvedClientDetailData = hasParentClientDetail
    ? clientDetailData
    : fetchedClientDetailData;
  const resolvedClientDetailError = hasParentClientDetail
    ? clientDetailError
    : fetchedClientDetailError;
  const resolvedClientDetailLoading = hasParentClientDetail
    ? clientDetailLoading
    : fetchedClientDetailLoading;

  useEffect(() => {
    setClientUsersLocal([]);
    setClientNameLocal("");
    setHasLoadedClient(false);
    setSelectedClientUser(null);
    setIsAssignModalOpen(false);
  }, [clientId]);

  useEffect(() => {
    if (!resolvedClientDetailData) return;
    const details = Array.isArray(resolvedClientDetailData.client_users_details)
      ? resolvedClientDetailData.client_users_details
      : [];
    setClientUsersLocal(details);
    setClientNameLocal(resolvedClientDetailData.name || "");
    setHasLoadedClient(true);
  }, [resolvedClientDetailData]);

  const loadingClientDetail =
    !!clientId &&
    !resolvedClientDetailError &&
    !hasLoadedClient &&
    resolvedClientDetailLoading;

  const clientUsersDetails = useMemo(
    () => clientUsersLocal,
    [clientUsersLocal]
  );

  const agencyId =
    resolvedClientDetailData?.agency ||
    resolvedClientDetailData?.agency_id ||
    null;

  const agencyClientsEndpoint =
    interceptorReady && agencyId
      ? `clients/options/?agency=${agencyId}&ordering=name&archived=all`
      : null;

  const { data: agencyClientsData, isLoading: agencyClientsLoading } = getData(
    agencyClientsEndpoint,
    !!agencyClientsEndpoint && isAssignModalOpen,
    [agencyId, refreshKey, refreshClientDetail]
  );

  const agencyClients = useMemo(() => {
    return Array.isArray(agencyClientsData) ? agencyClientsData : [];
  }, [agencyClientsData]);

  const handleOpenAssignModal = (clientUser) => {
    setSelectedClientUser(clientUser);
    setIsAssignModalOpen(true);
  };

  const updateClientUsersLocal = (userId, updater) => {
    setClientUsersLocal((prev) =>
      prev.map((user) => {
        if (user.id !== userId) return user;
        return {
          ...user,
          clients: updater(buildClientList(user)),
        };
      })
    );
    setSelectedClientUser((prev) => {
      if (!prev || prev.id !== userId) return prev;
      return {
        ...prev,
        clients: updater(buildClientList(prev)),
      };
    });
  };

  const handleAssignModalSuccess = (action, clientData) => {
    const removedCurrentClient =
      action === "remove" &&
      clientData?.id &&
      clientId &&
      String(clientData.id) === String(clientId);

    if (removedCurrentClient && selectedClientUser?.id) {
      setClientUsersLocal((prev) =>
        prev.filter((user) => user.id !== selectedClientUser.id)
      );
      setSelectedClientUser(null);
      setIsAssignModalOpen(false);
    }

    if (selectedClientUser?.id) {
      if (action === "assign" && clientData?.id) {
        updateClientUsersLocal(selectedClientUser.id, (clients) => {
          if (clients.some((client) => client.id === clientData.id)) {
            return clients;
          }
          return [...clients, clientData];
        });
      }
      if (action === "remove" && clientData?.id) {
        updateClientUsersLocal(selectedClientUser.id, (clients) =>
          clients.filter((client) => client.id !== clientData.id)
        );
      }
    }

    setRefreshClientDetail((prev) => prev + 1);

    if (onSuccess) {
      const message =
        action === "assign"
          ? "Cliente asignado correctamente"
          : "Cliente desasignado correctamente";
      onSuccess(message);
    }
  };

  if (loadingClientDetail) {
    return (
      <Card>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            padding: 6,
            textAlign: "center",
            minHeight: "260px",
          }}
        >
          <CircularProgress />
          <Typography
            variant="body1"
            sx={{ color: theme.palette.text.secondary }}
          >
            Cargando usuarios clientes...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader sx={{ pb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <InfoTooltip
                  title="En esta sección se muestran todos los usuarios vinculados al cliente. Además, se permite vincular estos usuarios a otros clientes con el botón '+'."
                >
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                    <Users size={18} />
                    {clientNameLocal
                      ? `Usuarios vinculados al cliente ${clientNameLocal}`
                      : "Usuarios vinculados al cliente"}
                  </Box>
                </InfoTooltip>
              </Typography>
            </Box>
          </Box>
        </CardHeader>

        <CardContent>
          {resolvedClientDetailError && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.error.light}22`,
                padding: 2,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: `1px solid ${theme.palette.error.light}`,
                mb: 2,
              }}
            >
              <AlertCircle size={16} color={theme.palette.error.main} />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.error.dark, fontSize: "0.875rem" }}
              >
                No se pudieron cargar los usuarios cliente.
              </Typography>
            </Box>
          )}

          {clientUsersDetails.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              {clientUsersDetails.map((clientUser) => (
                <ClientUserCard
                  key={clientUser.id}
                  clientUser={clientUser}
                  onAssignClients={handleOpenAssignModal}
                  canAssign={canAssignClientUsers}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ py: 3 }}>
              <EmptyStateMessage
                icon={Users}
                message="Aún no hay usuarios cliente"
              />
            </Box>
          )}

          {/* Información adicional */}
          <Box
            sx={{
              mt: 4,
              backgroundColor: `${colors.info.light_blue}22`,
              padding: 2,
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                color: colors.info.dark_blue,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Plus size={16} />
              Cómo vincular a otros clientes
            </Typography>
            <Box
              component="ul"
              sx={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {[
                "Busca el usuario que deseas vincular a otro cliente",
                "Haz click en el botón '+' que aparece junto a sus etiquetas",
                "Selecciona el nuevo cliente en la ventana que se abrirá",
                "El usuario pasará a estar vinculado a múltiples clientes (multicuenta)",
                "Puedes eliminar cualquier vinculación desde esta misma vista",
              ].map((step, index) => (
                <Box
                  key={index}
                  component="li"
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    fontSize: "0.875rem",
                    color: colors.info.dark_blue,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: colors.info.main_blue,
                      fontWeight: 600,
                      minWidth: "16px",
                    }}
                  >
                    {index + 1}.
                  </Typography>
                  <Typography component="span" sx={{ fontSize: "0.875rem" }}>
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <AssignClientModal
        open={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        clientUser={selectedClientUser}
        allClients={agencyClients}
        isClientsLoading={agencyClientsLoading}
        onSuccess={handleAssignModalSuccess}
        onError={onError}
      />
    </>
  );
}
